// src/pages/Users/Inscription/components/GroupSelectionPanel.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBirthdayCake,
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaPlus,
  FaRedoAlt,
  FaSearch,
  FaSlidersH,
} from "react-icons/fa";

import StepCard from "./StepCard";
import GroupChoiceCard from "./GroupChoiceCard";
import SelectedGroupCard from "./SelectedGroupCard";

import { normalizeGroupYears } from "../utils/inscriptionUtils";

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const normalizeDiscipline = (value) => normalizeText(value);

const getUniqueDisciplines = (groups = []) => {
  const map = new Map();

  groups.forEach((group) => {
    const discipline = String(group?.discipline_groupe || "").trim();

    if (!discipline) return;

    const key = normalizeDiscipline(discipline);

    if (!map.has(key)) {
      map.set(key, discipline);
    }
  });

  return Array.from(map.values()).sort((a, b) =>
    a.localeCompare(b, "fr", {
      sensitivity: "base",
    })
  );
};

const getUniqueYears = (groups = []) => {
  const years = new Set();

  groups.forEach((group) => {
    normalizeGroupYears(group?.annees_naissance).forEach((year) => {
      if (
        year !== null &&
        year !== undefined &&
        String(year).trim()
      ) {
        years.add(String(year).trim());
      }
    });
  });

  return Array.from(years).sort((a, b) => Number(b) - Number(a));
};

const groupMatchesYear = (group, year) => {
  if (!year) return true;

  const years = normalizeGroupYears(
    group?.annees_naissance
  ).map(String);

  return years.includes(String(year));
};

/* -------------------------------------------------------------------------- */
/*                               FilterSection                                */
/* -------------------------------------------------------------------------- */

function FilterSection({ icon, title, children }) {
  return (
    <div>
      <div className="mb-2.5 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 text-sm text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          {icon}
        </span>

        <p className="text-xs font-extrabold uppercase tracking-[0.13em] text-slate-500 dark:text-slate-400">
          {title}
        </p>
      </div>

      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                           CollapsedGroupBrowser                            */
/* -------------------------------------------------------------------------- */

function CollapsedGroupBrowser({
  selectedCount,
  totalGroups,
  onOpen,
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="
        group w-full overflow-hidden rounded-[1.7rem]
        border border-slate-200 bg-slate-50/80
        text-left transition
        hover:border-indigo-200 hover:bg-indigo-50/60
        dark:border-slate-800 dark:bg-slate-950/60
        dark:hover:border-indigo-500/30 dark:hover:bg-indigo-500/5
      "
    >
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="
              flex h-11 w-11 shrink-0 items-center justify-center
              rounded-2xl bg-indigo-100 text-indigo-700
              transition group-hover:bg-indigo-600 group-hover:text-white
              dark:bg-indigo-500/10 dark:text-indigo-300
            "
          >
            <FaPlus />
          </span>

          <div className="min-w-0">
            <p className="font-bold text-slate-950 dark:text-white">
              Ajouter ou changer de groupe
            </p>

            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {selectedCount > 0
                ? `${selectedCount} groupe${
                    selectedCount > 1 ? "s" : ""
                  } actuellement sélectionné${
                    selectedCount > 1 ? "s" : ""
                  }.`
                : "Aucun groupe sélectionné."}{" "}
              {totalGroups > 0
                ? `${totalGroups} groupe${
                    totalGroups > 1 ? "s" : ""
                  } disponible${totalGroups > 1 ? "s" : ""}.`
                : ""}
            </p>
          </div>
        </div>

        <span
          className="
            inline-flex shrink-0 items-center justify-center gap-2
            rounded-2xl border border-slate-200 bg-white
            px-4 py-2.5 text-sm font-bold text-slate-700
            transition group-hover:border-indigo-200
            group-hover:text-indigo-700
            dark:border-slate-700 dark:bg-slate-900
            dark:text-slate-200
          "
        >
          Afficher les groupes
          <FaChevronDown className="text-xs" />
        </span>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*                           GroupSelectionPanel                              */
/* -------------------------------------------------------------------------- */

export default function GroupSelectionPanel({
  disabled,

  dojos,
  dojosById,

  selectedDojo,
  setSelectedDojo,

  groups,
  selectedGroups,

  onAddGroup,
  onRemoveGroup,
  onOpenGroupDetails,

  groupError,

  isQuotaActive,
  canRegisterToGroup,
  getGroupUnavailableReason,
}) {
  const [selectedDiscipline, setSelectedDiscipline] =
    useState("");

  const [selectedYear, setSelectedYear] = useState("");

  const [search, setSearch] = useState("");

  /*
   * Aucun groupe sélectionné :
   * recherche ouverte par défaut.
   *
   * Groupe déjà sélectionné :
   * recherche repliée.
   */
  const [groupBrowserOpen, setGroupBrowserOpen] = useState(
    () => !selectedGroups?.length
  );

  /*
   * Permet de savoir si un groupe vient réellement d'être ajouté,
   * sans refermer la recherche simplement parce que le composant
   * se re-render.
   */
  const previousSelectedCountRef = useRef(
    selectedGroups?.length || 0
  );

  /* ---------------------------------------------------------------------- */
  /*                                 Options                                */
  /* ---------------------------------------------------------------------- */

  const disciplines = useMemo(
    () => getUniqueDisciplines(groups),
    [groups]
  );

  const years = useMemo(
    () => getUniqueYears(groups),
    [groups]
  );

  const selectedIds = useMemo(
    () =>
      new Set(
        (selectedGroups || []).map((group) =>
          String(group.id)
        )
      ),
    [selectedGroups]
  );

  /* ---------------------------------------------------------------------- */
  /*             Ouvrir / fermer automatiquement le navigateur             */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    const currentCount = selectedGroups?.length || 0;
    const previousCount =
      previousSelectedCountRef.current;

    /*
     * Plus aucun groupe :
     * il faut forcément permettre d'en choisir un.
     */
    if (currentCount === 0) {
      setGroupBrowserOpen(true);
    }

    /*
     * Un nouveau groupe vient d'être sélectionné :
     * on replie automatiquement la recherche.
     */
    if (currentCount > previousCount) {
      setGroupBrowserOpen(false);
    }

    previousSelectedCountRef.current = currentCount;
  }, [selectedGroups?.length]);

  /* ---------------------------------------------------------------------- */
  /*                 Vérification des valeurs de filtres                    */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!selectedDiscipline) return;

    const exists = disciplines.some(
      (discipline) =>
        normalizeDiscipline(discipline) ===
        normalizeDiscipline(selectedDiscipline)
    );

    if (!exists) {
      setSelectedDiscipline("");
    }
  }, [disciplines, selectedDiscipline]);

  useEffect(() => {
    if (!selectedYear) return;

    if (!years.includes(String(selectedYear))) {
      setSelectedYear("");
    }
  }, [years, selectedYear]);

  /* ---------------------------------------------------------------------- */
  /*                               Filtrage                                 */
  /* ---------------------------------------------------------------------- */

  const matchingGroups = useMemo(() => {
    let result = Array.isArray(groups)
      ? [...groups]
      : [];

    /*
     * Dojo
     */
    if (selectedDojo) {
      result = result.filter(
        (group) =>
          String(group.dojo_id) ===
          String(selectedDojo)
      );
    }

    /*
     * Discipline
     */
    if (selectedDiscipline) {
      result = result.filter(
        (group) =>
          normalizeDiscipline(
            group.discipline_groupe
          ) ===
          normalizeDiscipline(selectedDiscipline)
      );
    }

    /*
     * Année de naissance
     */
    if (selectedYear) {
      result = result.filter((group) =>
        groupMatchesYear(group, selectedYear)
      );
    }

    /*
     * Recherche texte
     */
    const searchValue = normalizeText(search);

    if (searchValue) {
      result = result.filter((group) => {
        const dojo =
          dojosById?.[String(group.dojo_id)];

        const searchable = [
          group.nom,
          group.discipline_groupe,
          group.annees_naissance,
          dojo?.dojo_nom,
        ]
          .map(normalizeText)
          .join(" ");

        return searchable.includes(searchValue);
      });
    }

    /*
     * Groupes ouverts en premier.
     */
    return result.sort((a, b) => {
      const aAvailable = canRegisterToGroup(a)
        ? 1
        : 0;

      const bAvailable = canRegisterToGroup(b)
        ? 1
        : 0;

      if (aAvailable !== bAvailable) {
        return bAvailable - aAvailable;
      }

      return String(a.nom || "").localeCompare(
        String(b.nom || ""),
        "fr",
        {
          sensitivity: "base",
        }
      );
    });
  }, [
    groups,
    selectedDojo,
    selectedDiscipline,
    selectedYear,
    search,
    dojosById,
    canRegisterToGroup,
  ]);

  /*
   * Les groupes déjà choisis disparaissent de la liste.
   */
  const visibleGroups = useMemo(
    () =>
      matchingGroups.filter(
        (group) =>
          !selectedIds.has(String(group.id))
      ),
    [matchingGroups, selectedIds]
  );

  const availableCount = useMemo(
    () =>
      visibleGroups.filter((group) =>
        canRegisterToGroup(group)
      ).length,
    [visibleGroups, canRegisterToGroup]
  );

  const hasFilters = Boolean(
    selectedDojo ||
      selectedDiscipline ||
      selectedYear ||
      String(search || "").trim()
  );

  /* ---------------------------------------------------------------------- */
  /*                                Actions                                 */
  /* ---------------------------------------------------------------------- */

  const resetFilters = () => {
    setSelectedDojo("");
    setSelectedDiscipline("");
    setSelectedYear("");
    setSearch("");
  };

  const handleAddGroup = (group) => {
    /*
     * Appel de ta logique existante.
     */
    onAddGroup(group);

    /*
     * On ferme immédiatement la zone.
     * Le useEffect confirmera également sa fermeture
     * quand selectedGroups sera mis à jour par le parent.
     */
    setGroupBrowserOpen(false);
  };

  const handleRemoveGroup = (groupId) => {
    onRemoveGroup(groupId);
  };

  /* ---------------------------------------------------------------------- */
  /*                                 Render                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <StepCard
      step="2"
      title="Choisissez votre groupe"
      icon={<FaLayerGroup />}
      description="Indiquez votre lieu de pratique, votre discipline et votre année de naissance : seuls les groupes qui vous correspondent seront proposés."
    >
      {disabled ? (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          Choisissez d’abord le type
          d’inscription à l’étape 1.
        </div>
      ) : (
        <div className="space-y-5">
          {/* -------------------------------------------------------------- */}
          {/*                            Erreur                              */}
          {/* -------------------------------------------------------------- */}

          {groupError ? (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
              {groupError}
            </div>
          ) : null}

          {/* -------------------------------------------------------------- */}
          {/*                   Groupes sélectionnés                        */}
          {/* -------------------------------------------------------------- */}

          {selectedGroups.length > 0 ? (
            <div className="rounded-[2rem] border border-emerald-200 bg-emerald-50/60 p-4 sm:p-5 dark:border-emerald-500/20 dark:bg-emerald-500/5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
                    Votre sélection
                  </p>

                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    {selectedGroups.length} groupe
                    {selectedGroups.length > 1
                      ? "s"
                      : ""}{" "}
                    sélectionné
                    {selectedGroups.length > 1
                      ? "s"
                      : ""}
                    .
                  </p>
                </div>

                <span className="flex h-10 min-w-10 items-center justify-center rounded-2xl bg-emerald-600 px-3 text-sm font-black text-white">
                  {selectedGroups.length}
                </span>
              </div>

              <div className="grid gap-3 lg:grid-cols-2">
                {selectedGroups.map((group) => (
                  <SelectedGroupCard
                    key={group.id}
                    group={group}
                    dojo={
                      dojosById[
                        String(group.dojo_id)
                      ]
                    }
                    onRemove={() =>
                      handleRemoveGroup(group.id)
                    }
                    onOpenDetails={() =>
                      onOpenGroupDetails?.(group)
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}

          {/* -------------------------------------------------------------- */}
          {/*                  NAVIGATEUR REPLIÉ                            */}
          {/* -------------------------------------------------------------- */}

          {!groupBrowserOpen &&
          selectedGroups.length > 0 ? (
            <CollapsedGroupBrowser
              selectedCount={selectedGroups.length}
              totalGroups={visibleGroups.length}
              onOpen={() =>
                setGroupBrowserOpen(true)
              }
            />
          ) : null}

          {/* -------------------------------------------------------------- */}
          {/*                  NAVIGATEUR DE GROUPES                        */}
          {/* -------------------------------------------------------------- */}

          {groupBrowserOpen ? (
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              {/* ---------------------------------------------------------- */}
              {/* Header                                                    */}
              {/* ---------------------------------------------------------- */}

              <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5 dark:border-slate-800 dark:bg-slate-950/50">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#013E8E] text-white">
                    <FaSlidersH />
                  </span>

                  <div>
                    <h3 className="font-bold text-slate-950 dark:text-white">
                      Rechercher un groupe
                    </h3>

                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Utilisez les filtres pour
                      trouver rapidement le cours
                      adapté.
                    </p>
                  </div>
                </div>

                {selectedGroups.length > 0 ? (
                  <button
                    type="button"
                    onClick={() =>
                      setGroupBrowserOpen(false)
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <FaChevronUp />
                    Masquer les groupes
                  </button>
                ) : null}
              </div>

              {/* ---------------------------------------------------------- */}
              {/* Corps                                                      */}
              {/* ---------------------------------------------------------- */}

              <div className="grid gap-5 p-4 sm:p-5 xl:grid-cols-[300px_minmax(0,1fr)]">
                {/* -------------------------------------------------------- */}
                {/* FILTRES                                                  */}
                {/* -------------------------------------------------------- */}

                <aside className="self-start rounded-[1.7rem] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/60">
                  <div className="space-y-5">
                    {/* Dojo */}

                    <FilterSection
                      icon={<FaMapMarkerAlt />}
                      title="Lieu de pratique"
                    >
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedDojo("")
                          }
                          className={`w-full rounded-2xl border px-3 py-2.5 text-left text-sm font-bold transition ${
                            !selectedDojo
                              ? "border-[#013E8E] bg-blue-50 text-[#013E8E] ring-2 ring-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/10"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                          }`}
                        >
                          Tous les lieux
                        </button>

                        {dojos.map((dojo) => {
                          const active =
                            String(
                              selectedDojo
                            ) ===
                            String(dojo.id);

                          return (
                            <button
                              key={dojo.id}
                              type="button"
                              onClick={() =>
                                setSelectedDojo(
                                  String(dojo.id)
                                )
                              }
                              className={`flex w-full items-center gap-2 rounded-2xl border px-3 py-2.5 text-left text-sm font-bold transition ${
                                active
                                  ? "border-[#013E8E] bg-blue-50 text-[#013E8E] ring-2 ring-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/10"
                                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                              }`}
                            >
                              <FaMapMarkerAlt className="shrink-0 text-xs" />

                              <span className="truncate">
                                {
                                  dojo.dojo_nom
                                }
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </FilterSection>

                    {/* Discipline */}

                    <FilterSection
                      icon={<FaLayerGroup />}
                      title="Discipline"
                    >
                      <select
                        value={
                          selectedDiscipline
                        }
                        onChange={(event) =>
                          setSelectedDiscipline(
                            event.target.value
                          )
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:ring-indigo-500/10"
                      >
                        <option value="">
                          Toutes les disciplines
                        </option>

                        {disciplines.map(
                          (discipline) => (
                            <option
                              key={discipline}
                              value={discipline}
                            >
                              {discipline}
                            </option>
                          )
                        )}
                      </select>
                    </FilterSection>

                    {/* Année */}

                    <FilterSection
                      icon={<FaBirthdayCake />}
                      title="Année de naissance"
                    >
                      <select
                        value={selectedYear}
                        onChange={(event) =>
                          setSelectedYear(
                            event.target.value
                          )
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:ring-indigo-500/10"
                      >
                        <option value="">
                          Toutes les années
                        </option>

                        {years.map((year) => (
                          <option
                            key={year}
                            value={year}
                          >
                            Né(e) en {year}
                          </option>
                        ))}
                      </select>

                      <p className="mt-2 text-[11px] leading-4 text-slate-400">
                        Sélectionnez l’année de
                        naissance pour afficher les
                        groupes adaptés.
                      </p>
                    </FilterSection>

                    {/* Recherche */}

                    <FilterSection
                      icon={<FaSearch />}
                      title="Recherche"
                    >
                      <div className="relative">
                        <FaSearch className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400" />

                        <input
                          type="text"
                          value={search}
                          onChange={(event) =>
                            setSearch(
                              event.target.value
                            )
                          }
                          placeholder="Ex : Poussins..."
                          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-9 pr-3 text-sm font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:ring-indigo-500/10"
                        />
                      </div>
                    </FilterSection>

                    {/* Reset */}

                    {hasFilters ? (
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                      >
                        <FaRedoAlt />
                        Réinitialiser les
                        filtres
                      </button>
                    ) : null}
                  </div>
                </aside>

                {/* -------------------------------------------------------- */}
                {/* RESULTATS                                                */}
                {/* -------------------------------------------------------- */}

                <div className="min-w-0">
                  {/* Header résultats */}

                  <div className="mb-4 flex flex-col gap-3 rounded-[1.5rem] border border-slate-200 bg-slate-50/60 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                        <FaFilter />
                      </span>

                      <div>
                        <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                          Groupes correspondant à
                          votre recherche
                        </h3>

                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          Les groupes ouverts sont
                          affichés en premier.
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <span className="rounded-full bg-slate-200/70 px-3 py-1.5 text-xs font-black text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {visibleGroups.length}{" "}
                        résultat
                        {visibleGroups.length > 1
                          ? "s"
                          : ""}
                      </span>

                      {availableCount > 0 ? (
                        <span className="rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-black text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                          {availableCount} ouvert
                          {availableCount > 1
                            ? "s"
                            : ""}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* Année sélectionnée */}

                  {selectedYear ? (
                    <div className="mb-4 rounded-[1.5rem] border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-500/20 dark:bg-blue-500/10">
                      <div className="flex gap-3">
                        <FaBirthdayCake className="mt-0.5 shrink-0 text-blue-600 dark:text-blue-300" />

                        <div>
                          <p className="text-sm font-bold text-blue-800 dark:text-blue-200">
                            Pratiquant né en{" "}
                            {selectedYear}
                          </p>

                          <p className="mt-1 text-xs leading-5 text-blue-700/80 dark:text-blue-300/80">
                            Les groupes ci-dessous
                            correspondent à cette
                            année de naissance.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* ------------------------------------------------------ */}
                  {/* Liste des groupes                                      */}
                  {/* ------------------------------------------------------ */}

                  {visibleGroups.length > 0 ? (
                    <div className="grid gap-4 lg:grid-cols-2">
                      {visibleGroups.map(
                        (group) => (
                          <GroupChoiceCard
                            key={group.id}
                            group={group}
                            dojo={
                              dojosById[
                                String(
                                  group.dojo_id
                                )
                              ]
                            }
                            isQuotaActive={
                              isQuotaActive
                            }
                            disabled={
                              !canRegisterToGroup(
                                group
                              )
                            }
                            unavailableReason={getGroupUnavailableReason(
                              group
                            )}
                            recommended={Boolean(
                              selectedYear
                            )}
                            onSelect={() =>
                              handleAddGroup(
                                group
                              )
                            }
                            onOpenDetails={() =>
                              onOpenGroupDetails?.(
                                group
                              )
                            }
                          />
                        )
                      )}
                    </div>
                  ) : matchingGroups.length >
                      0 &&
                    matchingGroups.length ===
                      selectedGroups.length ? (
                    <div className="rounded-[2rem] border border-emerald-200 bg-emerald-50 p-8 text-center dark:border-emerald-500/20 dark:bg-emerald-500/10">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-600 text-xl font-black text-white">
                        ✓
                      </div>

                      <h4 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
                        Groupe déjà sélectionné
                      </h4>

                      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600 dark:text-slate-300">
                        Tous les groupes
                        correspondant à cette
                        recherche sont déjà dans
                        votre sélection.
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950/60">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-200 text-xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                        <FaSearch />
                      </div>

                      <h4 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
                        Aucun groupe trouvé
                      </h4>

                      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                        Aucun groupe ne correspond
                        actuellement à l’ensemble
                        de vos critères.
                      </p>

                      {hasFilters ? (
                        <button
                          type="button"
                          onClick={resetFilters}
                          className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#012f6d]"
                        >
                          <FaRedoAlt />
                          Afficher tous les groupes
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </StepCard>
  );
}